/**
 * Toast: the bottom-LEFT notification stack.
 *
 * Renders from useToastStore. Place <ToastRegion /> once near the root, and
 * <ToastRouteWatcher /> beside it inside the router. A success or info toast
 * auto-dismisses on the clock its kind carries; a warn or error waits for the
 * reader (TOAST_AUTO_DISMISS_MS in the store, and read the note there before
 * putting a timer on a failure again).
 *
 * ── WHY IT IS ON THE LEFT, AND WHY IT LETS CLICKS THROUGH (round 11, P0) ─────
 *
 * The stack used to sit bottom-RIGHT with `pointerEvents: 'auto'` on the
 * container, and error toasts never expired. The pane grader raised two 402s
 * on two different trusts, changed route three times, and five minutes later
 * both toasts were still there, stacked over "Confirm and save to trust":
 * `document.elementFromPoint` at the centre of that button returned the toast
 * div, so the click on the screen's only primary action did nothing and fired
 * no request. Dismissing the toasts by hand made the same click work.
 *
 * Three things keep that closed, and all three are needed. None of them is a
 * timer: Paul ruled on 22 September that a failure still waits for the reader,
 * and what this finding proved is that a toast must not BLOCK or FOLLOW, which
 * is a different problem.
 *   1. The container is `pointerEvents: 'none'` and each toast `'auto'`, so a
 *      click anywhere outside a toast's own box reaches what is under it.
 *   2. The stack sits bottom-left: a screen's primary action is the
 *      right-hand end of its footer, which is the corner it was landing in.
 *   3. It is emptied on every route change (ToastRouteWatcher), because a
 *      toast is feedback about an action taken on ONE screen.
 *
 * WCAG: success/info toasts use role="status" (polite), warn/error use role="alert" (assertive).
 */

import { useEffect, useRef, useState } from 'react';
import { useLocation } from '@tanstack/react-router';
import { useToastStore, TOAST_AUTO_DISMISS_MS } from '../../lib/toast-store';
import type { Toast, ToastKind } from '../../lib/toast-store';

// ── Colour map ──────────────────────────────────────────────────────────────

interface ToneColors {
  bg: string;
  border: string;
  fg: string;
  progress: string;
}

const TONE: Record<ToastKind, ToneColors> = {
  success: { bg: 'var(--positive-soft)', border: 'var(--positive)', fg: 'var(--positive)', progress: 'var(--positive)' },
  info:    { bg: 'var(--primary-50)',        border: 'var(--primary-300)', fg: 'var(--primary-700)', progress: 'var(--primary-600)' },
  warn:    { bg: 'var(--accent-50)',       border: 'var(--accent-300)', fg: 'var(--accent-700)', progress: 'var(--accent-500)' },
  error:   { bg: 'var(--negative-soft)', border: 'var(--negative)', fg: 'var(--negative)', progress: 'var(--negative)' },
};

// ── Single toast item ────────────────────────────────────────────────────────

function ToastItem({ toast }: { toast: Toast }) {
  const dismiss = useToastStore((s) => s.dismissToast);
  const [progress, setProgress] = useState(100);
  const tone = TONE[toast.kind];

  // Shrink the progress bar over the SAME clock the store is counting down,
  // and render no bar at all for a kind that has none. It read a hardcoded
  // 4000 while a failure sat there forever, so the bar told every reader of a
  // failure that it was about to go, four times over.
  const duration = TOAST_AUTO_DISMISS_MS[toast.kind];
  useEffect(() => {
    if (duration === null) return;
    const ms = duration;
    const start = Date.now();
    let raf: number;
    function tick() {
      const elapsed = Date.now() - start;
      const remaining = Math.max(0, 100 - (elapsed / ms) * 100);
      setProgress(remaining);
      if (elapsed < ms) {
        raf = requestAnimationFrame(tick);
      }
    }
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [duration]);

  const isAlert = toast.kind === 'warn' || toast.kind === 'error';

  return (
    <div
      role={isAlert ? 'alert' : 'status'}
      aria-live={isAlert ? 'assertive' : 'polite'}
      aria-atomic="true"
      style={{
        position: 'relative',
        // The container below is click-through; each toast takes its own
        // clicks back so its Dismiss button still works.
        pointerEvents: 'auto',
        background: tone.bg,
        border: `1px solid ${tone.border}`,
        borderRadius: 'var(--r-md)',
        padding: '14px 40px 14px 16px',
        minWidth: 280,
        maxWidth: 360,
        boxShadow: '0 4px 24px rgba(20,18,12,0.10)',
        overflow: 'hidden',
        animation: 'toast-in 150ms var(--ease-cushion)',
      }}
    >
      {/* Message */}
      <div style={{ fontSize: 13.5, color: 'var(--ink)', lineHeight: 1.5, fontWeight: 500 }}>
        {toast.message}
      </div>

      {/* Reference pill */}
      {toast.ref && (
        <div
          className="mono"
          style={{
            marginTop: 6,
            fontSize: 11.5,
            color: tone.fg,
            padding: '2px 8px',
            background: 'rgba(20,18,12,0.06)',
            borderRadius: 4,
            display: 'inline-block',
          }}
        >
          {toast.ref}
        </div>
      )}

      {/* Dismiss */}
      <button
        aria-label="Dismiss notification"
        onClick={() => dismiss(toast.id)}
        style={{
          position: 'absolute',
          top: 10,
          right: 10,
          width: 22,
          height: 22,
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          color: 'var(--ink-3)',
          display: 'grid',
          placeItems: 'center',
          borderRadius: 4,
          padding: 0,
        }}
      >
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M1 1l10 10M11 1L1 11" />
        </svg>
      </button>

      {/* Progress bar. Absent for a kind that stays until dismissed: a bar
          that never moves is a promise the toast does not keep. */}
      {duration !== null && (
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          height: 3,
          width: `${progress}%`,
          background: tone.progress,
          transition: 'width 100ms linear',
          borderBottomLeftRadius: 'var(--r-md)',
        }}
      />
      )}
    </div>
  );
}

// ── Route-change clear ───────────────────────────────────────────────────────

/**
 * Empty the on-screen stack whenever the path changes, leaving the bell's
 * record alone. Takes the pathname as an argument rather than reading the
 * router itself, so it is testable without a RouterProvider and so ToastRegion
 * stays router-free (the a11y landmark test mounts that component bare).
 *
 * The FIRST render never clears: a toast raised by the navigation that just
 * happened ("Saved.", then straight to the next screen) is about the screen you
 * have landed on, not the one you left.
 */
export function useClearToastsOnRouteChange(pathname: string): void {
  const lastPath = useRef<string | null>(null);
  const clear = useToastStore((s) => s.clearToasts);
  useEffect(() => {
    if (lastPath.current !== null && lastPath.current !== pathname) clear();
    lastPath.current = pathname;
  }, [pathname, clear]);
}

/** Mount once inside the router, beside <ToastRegion />. Renders no DOM. */
export function ToastRouteWatcher(): null {
  const pathname = useLocation({ select: (l) => l.pathname });
  useClearToastsOnRouteChange(pathname);
  return null;
}

// ── Region (place once near root) ────────────────────────────────────────────

export function ToastRegion() {
  const toasts = useToastStore((s) => s.toasts);

  return (
    <>
      <style>{`
        @keyframes toast-in {
          from { opacity: 0; transform: translateY(12px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0)    scale(1); }
        }
      `}</style>
      {/* `role="region"` added 2026-08-22 (E2E a11y sweep): this div carried
          `aria-label` with NO role, which is invalid ARIA (a generic element
          takes no accessible name) — axe rates it serious and it fired on
          every authenticated page. `region` is the right role here, NOT
          `status`/`alert`: each ToastItem below is already its own live region
          with role="status" or role="alert", and nesting a live region inside
          another makes announcements double up. This wrapper is just the
          named container the toasts live in. */}
      <div
        role="region"
        aria-label="Notifications"
        style={{
          position: 'fixed',
          bottom: 24,
          // BOTTOM-LEFT (round 11): a screen's primary action is the right-hand
          // end of its footer, and that is the corner this stack was covering.
          left: 24,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          zIndex: 9000,
          // ALWAYS none, never 'auto'. The container is a positioning box the
          // width of its widest toast and the height of the stack; with 'auto'
          // it swallowed clicks in the gaps between toasts and in its own
          // padding. Each ToastItem sets 'auto' on itself.
          pointerEvents: 'none',
        }}
      >
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} />
        ))}
      </div>
    </>
  );
}
